<template>
  <el-dialog
    :visible="visible"
    width="540px"
    custom-class="launch-edit-modal"
    :show-close="true"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :modal-append-to-body="true"
    :append-to-body="true"
    :before-close="onClose"
    @close="onClose"
  >
    <div slot="title" class="lem__title">Review your details</div>

    <div class="lem__body">
      <!-- 1. Company -->
      <section class="lem__section">
        <header class="lem__section-head">
          <h3 class="lem__section-title">Company</h3>
          <button type="button" class="lem__edit-btn" @click="goto(1)">Edit</button>
        </header>
        <dl class="lem__rows">
          <div class="lem__row">
            <dt>Name</dt>
            <dd>{{ company.name || notSet }}</dd>
          </div>
          <div class="lem__row">
            <dt>Country</dt>
            <dd>{{ company.country || notSet }}</dd>
          </div>
          <div class="lem__row">
            <dt>Timezone</dt>
            <dd>{{ timezoneLabel }}</dd>
          </div>
          <div class="lem__row">
            <dt>Cadence</dt>
            <dd>{{ cadenceLabel }}</dd>
          </div>
          <div class="lem__row">
            <dt>Delivery time</dt>
            <dd>{{ deliveryTimeLabel }}</dd>
          </div>
        </dl>
      </section>

      <!-- 2. Core values -->
      <section class="lem__section">
        <header class="lem__section-head">
          <h3 class="lem__section-title">Core values</h3>
          <button type="button" class="lem__edit-btn" @click="goto(2)">Edit</button>
        </header>
        <div v-if="coreValues.length" class="lem__pills">
          <span
            v-for="(v, i) in coreValues"
            :key="i"
            class="lem__pill"
          >{{ v.name || v.label || '' }}</span>
        </div>
        <div v-else class="lem__muted">No core values yet.</div>
      </section>

      <!-- 3. People & teams -->
      <section class="lem__section">
        <header class="lem__section-head">
          <h3 class="lem__section-title">People &amp; teams</h3>
          <button type="button" class="lem__edit-btn" @click="goto(3)">Edit</button>
        </header>
        <dl class="lem__rows">
          <div class="lem__row">
            <dt>People</dt>
            <dd>{{ peopleTotal }} {{ peopleTotal === 1 ? 'person' : 'people' }}</dd>
          </div>
          <div class="lem__row">
            <dt>Teams</dt>
            <dd>{{ teamCount }}</dd>
          </div>
        </dl>
        <div v-if="teamBreakdown.length" class="lem__team-list">
          <div
            v-for="(t, idx) in teamBreakdown"
            :key="idx"
            class="lem__team"
          >
            <span class="lem__team-name">{{ t.name }}</span>
            <span class="lem__team-count">
              {{ t.count }} {{ t.count === 1 ? 'member' : 'members' }}
            </span>
          </div>
        </div>
      </section>

      <!-- 4. Rewards -->
      <section class="lem__section">
        <header class="lem__section-head">
          <h3 class="lem__section-title">Rewards</h3>
          <button type="button" class="lem__edit-btn" @click="goto(4)">Edit</button>
        </header>
        <dl class="lem__rows">
          <div class="lem__row">
            <dt>Selected</dt>
            <dd>
              <strong class="lem__reward-num">{{ selectedRewardIds.length }}</strong>
              <span class="lem__muted-inline" v-if="rewardMasterCount">
                of {{ rewardMasterCount }}
              </span>
            </dd>
          </div>
        </dl>
      </section>
    </div>

    <div slot="footer" class="lem__footer">
      <button type="button" class="lem__close-btn" @click="onClose">Close</button>
    </div>
  </el-dialog>
</template>

<script>
/**
 * LaunchEditModal — granular review summary, opened on demand from the
 * Step 6 launch screen via the "Edit details" link.
 *
 * Four sections: Company, Core values, People & teams, Rewards.
 * Each section has an Edit button that emits `goto-step` with the
 * relevant step number (1..4). Closes via the X, Esc, modal-click, or
 * the bottom Close button.
 *
 * Props:
 *  - visible:    Boolean   — controls v-model on the dialog
 *  - wizardData: Object    — full $store.state.signup.wizardData snapshot
 *
 * Emits:
 *  - close                 — close was requested
 *  - goto-step (Number)    — bubble up to wizard for routing
 */

const CADENCE_LABELS = {
  light: 'Light',
  balanced: 'Balanced',
  intense: 'Intense',
}

export default {
  name: 'LaunchEditModal',
  props: {
    visible:    { type: Boolean, default: false },
    wizardData: { type: Object,  default: () => ({}) },
  },
  data() {
    return { notSet: 'Not set' }
  },
  computed: {
    company() {
      return (this.wizardData && this.wizardData.company) || {}
    },
    coreValues() {
      const v = this.wizardData && this.wizardData.coreValues
      return Array.isArray(v) ? v : []
    },
    people() {
      const p = this.wizardData && this.wizardData.people
      return Array.isArray(p) ? p : []
    },
    selectedRewardIds() {
      const ids = this.wizardData && this.wizardData.selectedRewardIds
      return Array.isArray(ids) ? ids : []
    },
    timezoneLabel() {
      const tz = this.company.timezone
      if (tz === null || tz === undefined || tz === '') return this.notSet
      const n = Number(tz)
      if (Number.isNaN(n)) return String(tz)
      const sign = n >= 0 ? '+' : '-'
      const abs = Math.abs(n)
      const hours = Math.floor(abs)
      const minutes = Math.round((abs - hours) * 60)
      const mm = minutes === 0 ? '00' : String(minutes).padStart(2, '0')
      return `GMT${sign}${hours}:${mm}`
    },
    cadenceLabel() {
      const c = String((this.company && this.company.cadence) || 'balanced').toLowerCase()
      return CADENCE_LABELS[c] || 'Balanced'
    },
    deliveryTimeLabel() {
      const raw = (this.company && this.company.delivery_time) || ''
      return formatDeliveryTime(raw) || '8:00 AM'
    },
    peopleTotal() {
      return this.people.length
    },
    teamBreakdown() {
      const map = Object.create(null)
      this.people.forEach((p) => {
        const t = (p && p.team) || 'Unassigned'
        if (!map[t]) map[t] = 0
        map[t] += 1
      })
      return Object.keys(map).map((name) => ({ name, count: map[name] }))
    },
    teamCount() {
      return this.teamBreakdown.length
    },
    rewardMasterMap() {
      const raw = this.$store.state.register && this.$store.state.register.rewardMaster
      if (!raw) return {}
      if (Array.isArray(raw)) {
        const map = {}
        raw.forEach((r, i) => {
          const id = (r && (r.key || r.id)) || String(i)
          map[id] = r
        })
        return map
      }
      return raw
    },
    rewardMasterCount() {
      // Count perks-only rewards that match the company's country —
      // the same catalog the user saw on Step 4. Gift cards (`tag[0]
      // === '0'`) are excluded; "All Countries" / blank country is
      // universal. Mirrors the filter in WizardRewardsStep.
      const country = String((this.company && this.company.country) || '').trim().toUpperCase()
      const map = this.rewardMasterMap
      let count = 0
      for (const k of Object.keys(map)) {
        const r = map[k]
        if (!r) continue
        const tag = r.tag
        const tagKey =
          Array.isArray(tag) && tag.length ? String(tag[0]) :
          (typeof tag === 'string' || typeof tag === 'number') ? String(tag) : ''
        if (tagKey === '0') continue // gift card → not in signup scope
        const rc = r.country
        if (!rc) { count += 1; continue }
        const upper = String(rc).trim().toUpperCase()
        if (!upper || upper === 'ALL COUNTRIES' || !country || upper === country) {
          count += 1
        }
      }
      return count
    },
  },
  methods: {
    goto(stepNumber) {
      // Close the modal immediately so the wizard nav animation isn't
      // covered by the dialog overlay.
      this.$emit('goto-step', stepNumber)
      this.$emit('close')
    },
    onClose() {
      this.$emit('close')
    },
  },
}

function formatDeliveryTime(value) {
  if (!value || typeof value !== 'string') return ''
  const m = value.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return ''
  let h = parseInt(m[1], 10)
  const mins = m[2]
  if (Number.isNaN(h)) return ''
  const period = h >= 12 ? 'PM' : 'AM'
  h = h % 12
  if (h === 0) h = 12
  return `${h}:${mins} ${period}`
}
</script>

<!-- el-dialog teleports to body, so the popper styles must be unscoped. -->
<style lang="scss">
.launch-edit-modal {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(30, 10, 60, 0.06), 0 28px 56px -24px rgba(78, 20, 172, 0.18);
}
.launch-edit-modal .el-dialog__header {
  padding: 18px 22px 12px;
  border-bottom: 1px solid #ece8f4;
}
.launch-edit-modal .el-dialog__body {
  padding: 18px 22px 8px;
}
.launch-edit-modal .el-dialog__footer {
  padding: 12px 22px 18px;
  border-top: 1px solid #ece8f4;
}
.launch-edit-modal .lem__title {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #1a1a2e;
  letter-spacing: -0.1px;
}

.launch-edit-modal .lem__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.launch-edit-modal .lem__section {
  border: 1px solid #ece8f4;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
  transition: border-color 140ms ease;
}
.launch-edit-modal .lem__section:hover {
  border-color: #ddd5ee;
}

.launch-edit-modal .lem__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.launch-edit-modal .lem__section-title {
  margin: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}
.launch-edit-modal .lem__edit-btn {
  appearance: none;
  background: transparent;
  border: none;
  padding: 4px 8px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 800;
  color: #6C30CC;
  cursor: pointer;
  border-radius: 6px;
  transition: background 140ms ease;
}
.launch-edit-modal .lem__edit-btn:hover {
  background: rgba(108, 48, 204, 0.06);
  text-decoration: underline;
}
.launch-edit-modal .lem__edit-btn:focus-visible {
  outline: 2px solid #F7B924;
  outline-offset: 2px;
}

.launch-edit-modal .lem__rows {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 14px;
  margin: 0;
}
.launch-edit-modal .lem__row {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.launch-edit-modal .lem__row dt {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}
.launch-edit-modal .lem__row dd {
  margin: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  word-break: break-word;
}

.launch-edit-modal .lem__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.launch-edit-modal .lem__pill {
  background: rgba(108, 48, 204, 0.08);
  color: #6C30CC;
  border-radius: 999px;
  padding: 4px 10px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 700;
}
.launch-edit-modal .lem__muted {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  color: #6b7280;
}
.launch-edit-modal .lem__muted-inline {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin-left: 4px;
}

.launch-edit-modal .lem__team-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.launch-edit-modal .lem__team {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  border-bottom: 1px dashed #f0eef7;
}
.launch-edit-modal .lem__team:last-child {
  border-bottom: none;
}
.launch-edit-modal .lem__team-name {
  color: #1a1a2e;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.launch-edit-modal .lem__team-count {
  color: #6b7280;
  font-weight: 600;
  flex-shrink: 0;
}

.launch-edit-modal .lem__reward-num {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 900;
  color: #6C30CC;
}

.launch-edit-modal .lem__footer {
  display: flex;
  justify-content: flex-end;
}
.launch-edit-modal .lem__close-btn {
  appearance: none;
  background: transparent;
  border: 1px solid #ece8f4;
  border-radius: 10px;
  padding: 8px 16px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease;
}
.launch-edit-modal .lem__close-btn:hover {
  background: rgba(30, 10, 60, 0.04);
  border-color: #d5cde8;
}
.launch-edit-modal .lem__close-btn:focus-visible {
  outline: 2px solid #F7B924;
  outline-offset: 2px;
}
</style>
